import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Search, 
  BookOpen,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  DollarSign,
  CheckCircle,
  XCircle,
  ArrowRight,
  Save,
  X,
  ArrowLeft,
  UserPlus,
  AlertCircle
} from 'lucide-react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useStudents } from '../students/StudentProvider';
import { useClasses } from '../classes/ClassesContext';
import { useEnrollments } from './EnrollmentsProvider';
import enrollmentsService from '../../services/enrollmentsService';
import classSessionsService from '../../services/classSessionsService';

const EditEnrollment = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { students, loading: studentsLoading, fetchStudents } = useStudents();
  const { classes, loading: classesLoading, fetchClasses } = useClasses();
  const { enrollments, updateEnrollment, loading: enrollmentLoading, fetchEnrollments } = useEnrollments();
  
  const [enrollment, setEnrollment] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [enrollmentData, setEnrollmentData] = useState({
    notes: ''
  });
  const [notification, setNotification] = useState({ type: '', message: '', show: false });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [classSessions, setClassSessions] = useState({});

  // Load data on component mount
  useEffect(() => {
    fetchStudents();
    fetchClasses();
    fetchEnrollments();
  }, []);

  // Find the enrollment when data is loaded
  useEffect(() => {
    if (enrollments.length > 0 && id) {
      const foundEnrollment = enrollments.find(e => e.id === parseInt(id) || e.id === id);
      if (foundEnrollment) {
        setEnrollment(foundEnrollment);
        
        // Set enrollment data
        setEnrollmentData({
          notes: foundEnrollment.notes || ''
        });
        
        // Find and set the student
        const studentId = foundEnrollment.studentId || foundEnrollment.student_id;
        const student = students.find(s => s.id === studentId);
        if (student) {
          setSelectedStudent(student);
        }
        
        // Find and set the class
        const classId = foundEnrollment.classId || foundEnrollment.class_id;
        const classItem = classes.find(c => c.id === classId);
        if (classItem) {
          setSelectedClass(classItem);
        }
      }
    }
  }, [enrollments, students, classes, id]);

  // Load enrollment history
  useEffect(() => {
    const loadHistory = async () => {
      if (!id) return;
      
      try {
        setHistoryLoading(true);
        const historyData = await enrollmentsService.getEnrollmentHistory(id);
        setHistory(historyData || []);
      } catch (error) {
        console.error('Failed to load enrollment history:', error);
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [id]);

  // Load sessions for all classes when classes are loaded
  useEffect(() => {
    if (classes.length > 0) {
      classes.forEach(classItem => {
        loadSessionsForClass(classItem.id);
      });
    }
  }, [classes]);

  const showNotification = (type, message) => {
    setNotification({ type, message, show: true });
    setTimeout(() => {
      setNotification({ type: '', message: '', show: false });
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedClass) {
      showNotification('error', 'Please select a class.');
      return;
    }

    try {
      const updatedEnrollmentData = {
        classId: selectedClass.id,
        notes: enrollmentData.notes
      };

      await updateEnrollment(id, updatedEnrollmentData);
      showNotification('success', 'Enrollment updated successfully!');
      
      // Reload history to show the new changes
      try {
        const historyData = await enrollmentsService.getEnrollmentHistory(id);
        setHistory(historyData || []);
      } catch (error) {
        console.error('Failed to reload history:', error);
      }
      
      // Navigate back to enrollments list after a short delay
      setTimeout(() => {
        navigate('/enrollments');
      }, 1500);
    } catch (error) {
      console.error('Error updating enrollment:', error);
      showNotification('error', 'Failed to update enrollment. Please try again.');
    }
  };

  const getStudentDisplayName = (student) => {
    const firstName = student.studentFirstName || student.student_first_name || student.firstName || '';
    const lastName = student.studentLastName || student.student_last_name || student.lastName || '';
    return `${firstName} ${lastName}`.trim();
  };

  const getGenderPlaceholderStyle = (gender) => {
    switch (gender?.toLowerCase()) {
      case 'female':
        return 'bg-gradient-to-br from-pink-300 to-pink-500';
      case 'male':
        return 'bg-gradient-to-br from-blue-300 to-blue-500';
      default:
        return 'bg-gradient-to-br from-gray-300 to-gray-500';
    }
  };

  // Format session information for display (same as Enrollments.jsx)
  const formatSessionInfo = (classId) => {
    const sessions = classSessions[classId];
    
    if (!sessions) {
      return 'Loading...';
    }
    
    if (sessions.length === 0) {
      return 'No sessions scheduled';
    }

    // Sort sessions by date
    const sortedSessions = [...sessions].sort((a, b) => new Date(a.session_date) - new Date(b.session_date));
    const firstSession = sortedSessions[0];
    const sessionCount = sessions.length;
    
    if (sessionCount === 1) {
      const firstDate = new Date(firstSession.session_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      return firstDate;
    }

    const firstDate = new Date(firstSession.session_date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `${sessionCount} sessions, starts ${firstDate}`;
  };

  // Load sessions for a class
  const loadSessionsForClass = async (classId) => {
    if (classSessions[classId]) return; // Already loaded
    
    try {
      const result = await classSessionsService.getSessionsForClass(classId);
      
      if (result.success) {
        setClassSessions(prev => ({
          ...prev,
          [classId]: result.data || []
        }));
      } else {
        console.error('Failed to load sessions for class:', classId, result.error);
        setClassSessions(prev => ({
          ...prev,
          [classId]: []
        }));
      }
    } catch (error) {
      console.error('Failed to load sessions for class:', classId, error);
      setClassSessions(prev => ({
        ...prev,
        [classId]: []
      }));
    }
  };

  if (studentsLoading || classesLoading || enrollmentLoading || !enrollment) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-[#7A99D8] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading enrollment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UserPlus className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Edit Enrollment</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Modify enrollment details</p>
            </div>
          </div>
          <Link
            to="/enrollments"
            className="bg-[#F5F5F5] hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Enrollments
          </Link>
        </div>
      </div>

      {/* Notification */}
      {notification.show && (
        <div className={`mx-6 mt-6 p-4 rounded-lg border ${
          notification.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
        }`}>
          <div className="flex items-center gap-3">
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{notification.message}</span>
          </div>
        </div>
      )}

      <div className="p-6">
        <form id="edit-enrollment-form" onSubmit={handleSubmit}>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-8">
            
            {/* Current Student Section */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200 mb-4">Current Student</h3>
              {selectedStudent ? (
                <div className="bg-[#F5F5F5] dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    {/* Student Photo */}
                    <div className="flex-shrink-0">
                      {selectedStudent.photoUrl || selectedStudent.photo_url ? (
                        <img
                          src={selectedStudent.photoUrl || selectedStudent.photo_url}
                          alt={getStudentDisplayName(selectedStudent)}
                          className="w-16 h-16 rounded-lg object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-16 h-16 ${getGenderPlaceholderStyle(selectedStudent.gender)} rounded-lg flex items-center justify-center text-white text-lg font-medium`}
                        style={{ display: (selectedStudent.photoUrl || selectedStudent.photo_url) ? 'none' : 'flex' }}
                      >
                        {(selectedStudent.studentFirstName || selectedStudent.student_first_name || selectedStudent.firstName || '').charAt(0)}{(selectedStudent.studentLastName || selectedStudent.student_last_name || selectedStudent.lastName || '').charAt(0)}
                      </div>
                    </div>
                    
                    {/* Student Info */}
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200">
                        {getStudentDisplayName(selectedStudent)}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Age: {selectedStudent.studentAge || selectedStudent.student_age || selectedStudent.age} • 
                        Experience: {selectedStudent.experience || 'Not specified'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Parent: {selectedStudent.parentName || selectedStudent.parent_name} • 
                        Email: {selectedStudent.parentEmail || selectedStudent.parent_email}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                  <User className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No student selected</p>
                </div>
              )}
            </div>

            {/* Current Class Section */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200 mb-4">Current Class</h3>
              {enrollment && (() => {
                // Find the current class to get details
                const currentClass = classes.find(c => c.id === (enrollment.classId || enrollment.class_id));
                
                if (!currentClass) {
                  return (
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                      <BookOpen className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">Current class details not available</p>
                    </div>
                  );
                }
                
                return (
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 dark:bg-green-800 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#3C3C3C] dark:text-gray-200">{currentClass.title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Price: ${currentClass.price} • 
                          Min Age: {currentClass.minimumAge || 'Any'}+ • 
                          Level: {currentClass.skillLevel || 'All Levels'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Instructor: {currentClass.instructor} • 
                          Duration: {currentClass.duration} hours
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Dates: {formatSessionInfo(currentClass.id)}
                        </p>
                      </div>
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Select New Class Section */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200 mb-4">Change to Different Class</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#3C3C3C] dark:text-gray-200 mb-2">
                  Select New Class
                </label>
                <select
                  value={selectedClass?.id || ''}
                  onChange={(e) => {
                    const classId = e.target.value;
                    const classItem = classes.find(c => c.id === classId);
                    setSelectedClass(classItem || null);
                  }}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent text-sm"
                >
                  <option value="">Choose a different class...</option>
                  {classes
                    .filter(classItem => classItem.id !== (enrollment?.classId || enrollment?.class_id))
                    .map(classItem => {
                      const dateStr = formatSessionInfo(classItem.id);
                      
                      return (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.title} - ${classItem.price} • {dateStr} • Age {classItem.minimumAge || 'Any'}+ • {classItem.skillLevel || 'All Levels'}
                        </option>
                      );
                    })}
                </select>
              </div>

              {/* Selected Class Preview */}
              {selectedClass && selectedClass.id !== (enrollment?.classId || enrollment?.class_id) && (
                <div className="bg-[#FFECC5] dark:bg-blue-900/20 border border-[#FFE4A3] dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#FFE4A3] dark:bg-blue-800 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-6 h-6 text-[#3C3C3C] dark:text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#3C3C3C] dark:text-gray-200">{selectedClass.title}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Price: ${selectedClass.price} • 
                        Min Age: {selectedClass.minimumAge || 'Any'}+ • 
                        Level: {selectedClass.skillLevel || 'All Levels'}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Instructor: {selectedClass.instructor} • 
                        Duration: {selectedClass.duration} hours
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Dates: {formatSessionInfo(selectedClass.id)}
                      </p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-[#3C3C3C] dark:text-blue-400" />
                  </div>
                </div>
              )}
            </div>

            {/* Enrollment Notes */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200 mb-4">Additional Notes</h3>
              <textarea
                value={enrollmentData.notes}
                onChange={(e) => setEnrollmentData(prev => ({ ...prev, notes: e.target.value }))}
                rows={4}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7A99D8] focus:border-transparent"
                placeholder="Add any additional notes about this enrollment change..."
              />
            </div>

            {/* Enrollment History */}
            <div>
              <h3 className="text-lg font-semibold text-[#3C3C3C] dark:text-gray-200 mb-4">Change History</h3>
              {historyLoading ? (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                  <div className="w-6 h-6 border-2 border-gray-200 dark:border-gray-600 border-t-[#7A99D8] rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading history...</p>
                </div>
              ) : history.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {history.map((change, index) => (
                    <div key={change.id || index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-2 h-2 rounded-full ${
                              change.change_type === 'class_changed' ? 'bg-blue-500' :
                              change.change_type === 'payment_updated' ? 'bg-green-500' :
                              change.change_type === 'status_changed' ? 'bg-yellow-500' :
                              change.change_type === 'notes_updated' ? 'bg-purple-500' :
                              'bg-gray-500'
                            }`}></div>
                            <span className="text-sm font-medium text-[#3C3C3C] dark:text-gray-200">
                              {change.description}
                            </span>
                          </div>
                          
                          {change.change_type === 'class_changed' && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4">
                              <div>From: {(() => {
                                try {
                                  const oldValue = JSON.parse(change.old_value);
                                  return oldValue.name || 'Unknown Class';
                                } catch {
                                  return change.old_value;
                                }
                              })()}</div>
                              <div>To: {(() => {
                                try {
                                  const newValue = JSON.parse(change.new_value);
                                  return newValue.name || 'Unknown Class';
                                } catch {
                                  return change.new_value;
                                }
                              })()}</div>
                            </div>
                          )}
                          
                          {change.change_type !== 'class_changed' && change.old_value && change.new_value && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 ml-4">
                              <div>From: {change.old_value}</div>
                              <div>To: {change.new_value}</div>
                            </div>
                          )}
                        </div>
                        
                        <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                          <div>{new Date(change.created_at).toLocaleDateString()}</div>
                          <div>{new Date(change.created_at).toLocaleTimeString()}</div>
                          {change.changed_by && (
                            <div className="mt-1">by {change.changed_by}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-8 text-center">
                  <Clock className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No changes recorded yet</p>
                </div>
              )}
            </div>

            {/* Spacer for sticky buttons */}
            <div className="h-20"></div>
          </div>
        </form>
      </div>

      {/* Sticky Form Actions */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 shadow-lg z-10">
        <div className="max-w-4xl mx-auto flex justify-end space-x-4">
          <Link
            to="/enrollments"
            className="bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-[#3C3C3C] dark:text-gray-200 px-6 py-2 rounded-lg transition-colors font-medium flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Cancel
          </Link>
          <button
            type="submit"
            form="edit-enrollment-form"
            disabled={enrollmentLoading}
            className="bg-[#FFECC5] hover:bg-[#FFE4A3] dark:bg-blue-600 dark:hover:bg-blue-700 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-500 dark:disabled:text-gray-400 text-[#3C3C3C] dark:text-white px-6 py-2 rounded-lg transition-colors font-medium flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {enrollmentLoading ? 'Updating...' : 'Update Enrollment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditEnrollment;